import { Badge, makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import { Briefcase16Regular, Eye16Regular, Target16Regular } from '@fluentui/react-icons'
import { EditableField } from '../../components/EditableField'
import { DemandTagPicker } from '../../components/DemandTagPicker'
import {
  RequestAnswers,
  hasRequestAnswers,
  type RequestAnswersProps,
} from '../../components/RequestAnswers'
import { useDemandAttachments } from '../../hooks/useDemandAttachments'
import type { Demand } from '../../types/demand'
import { PITCH_LABELS } from '../../lib/labels'
import { bayerColors } from '../../theme'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  section: {
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    padding: tokens.spacingVerticalL,
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: bayerColors.navy,
    margin: 0,
    marginBottom: tokens.spacingVerticalM,
  },
  kvRow: {
    display: 'grid',
    gridTemplateColumns: '180px 1fr',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} 0`,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke3,
    ':last-child': {
      borderBottomStyle: 'none',
    },
  },
  kvKey: {
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '.04em',
  },
  kvValue: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
    lineHeight: 1.5,
  },
  sectionHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalM,
  },
  flush: {
    marginBottom: 0,
  },
  pitchKey: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  problemBody: {
    fontSize: '14px',
    lineHeight: 1.6,
    color: tokens.colorNeutralForeground1,
  },
})

export interface OverviewTabProps {
  demand: Demand
  canEdit: boolean
  /** Benefit, criticality and tags: the server takes them from council only.
   *  Defaults to `canEdit`. */
  canEditCouncilFields?: boolean
  /** The request's own words (description, the form's answers) — wider than
   *  `canEdit` by the requestor, who may reword their own open demand. */
  canEditRequest?: boolean
  onSave: (path: string, value: string | string[] | number) => void
  /** For a viewer with no Business case tab: show the two intake-form answers
   *  that otherwise live only there. The server returns them to the demand's
   *  own submitter and to nobody else outside council (#1365). */
  showIntakeAnswers?: boolean
}

// What talent reads before applying (#1499). The role itself is per position (#1457).
const PITCH_ROWS = [
  {
    path: 'pitch_project',
    Icon: Briefcase16Regular,
    placeholder: 'What the project is, in a few sentences',
  },
  {
    path: 'pitch_impact',
    Icon: Target16Regular,
    placeholder: 'What changes if it succeeds',
  },
] as const

/**
 * Demand detail Overview tab — problem statement + success metrics.
 * Needs/effort/skills moved to the Talent tab.
 */
export function OverviewTab({
  demand,
  canEdit,
  canEditCouncilFields = canEdit,
  canEditRequest = canEdit,
  onSave,
  showIntakeAnswers,
}: OverviewTabProps) {
  const styles = useStyles()
  // Background and anticipated benefit sit on the server's council-only list,
  // with the requestor's own words let through: council edits them, and so does
  // a requestor who is no caretaker. A caretaker alone is refused, so is not
  // offered the field.
  const canEditAnswers = canEditCouncilFields || (canEditRequest && !canEdit)
  const { data: attachments } = useDemandAttachments(demand.id)
  // Questions 3 and 6 and the files (#1628, #1629). The server leaves them
  // empty for a viewer who may not read them, so presence is the gate.
  const answers: RequestAnswersProps = {
    relatedWork: demand.related_work,
    relatedWorkDetails: demand.related_work_details,
    importantDate: demand.important_date,
    importantDateReason: demand.important_date_reason,
    attachments,
  }

  const pitchRows = PITCH_ROWS.filter((r) => canEdit || demand[r.path])

  return (
    <div className={styles.root}>
      {pitchRows.length > 0 && (
        <section className={styles.section} data-testid="overview-pitch">
          <div className={styles.sectionHead}>
            <h3 className={mergeClasses(styles.sectionTitle, styles.flush)}>
              About this opportunity
            </h3>
            {canEdit && (
              <Badge appearance="tint" color="informative" icon={<Eye16Regular />}>
                Shown to talent
              </Badge>
            )}
          </div>
          {pitchRows.map(({ path, Icon, placeholder }) => (
            <div key={path} className={styles.kvRow}>
              <span className={mergeClasses(styles.kvKey, styles.pitchKey)}>
                <Icon aria-hidden />
                {PITCH_LABELS[path]}
              </span>
              <div className={styles.kvValue}>
                <EditableField
                  value={demand[path] ?? ''}
                  path={path}
                  onSave={onSave}
                  disabled={!canEdit}
                  multiline
                  placeholder={placeholder}
                  ariaLabel={PITCH_LABELS[path]}
                />
              </div>
            </div>
          ))}
        </section>
      )}

      <section className={styles.section} data-testid="overview-problem">
        <h3 className={styles.sectionTitle}>The problem</h3>
        <div className={styles.problemBody}>
          <EditableField
            value={demand.description}
            path="description"
            onSave={onSave}
            disabled={!canEditRequest}
            multiline
            placeholder="Describe the problem being addressed…"
            ariaLabel="Problem description"
          />
        </div>
      </section>

      {showIntakeAnswers && (demand.target_user || demand.background || canEditRequest) && (
        <section className={styles.section} data-testid="overview-intake-answers">
          <h3 className={styles.sectionTitle}>More detail you gave</h3>
          {(demand.target_user || canEditRequest) && (
            <div className={styles.kvRow}>
              <span className={styles.kvKey}>Who it is for</span>
              <div className={styles.kvValue}>
                <EditableField
                  value={demand.target_user ?? ''}
                  path="target_user"
                  onSave={onSave}
                  disabled={!canEditRequest}
                  placeholder="Who would benefit?"
                  ariaLabel="Who it is for"
                />
              </div>
            </div>
          )}
          {(demand.background || canEditAnswers) && (
            <div className={styles.kvRow}>
              <span className={styles.kvKey}>Background</span>
              <div className={styles.kvValue}>
                <EditableField
                  value={demand.background ?? ''}
                  path="background"
                  onSave={onSave}
                  disabled={!canEditAnswers}
                  multiline
                  placeholder="Anything else worth knowing?"
                  ariaLabel="Background"
                />
              </div>
            </div>
          )}
        </section>
      )}

      {hasRequestAnswers(answers) && (
        <section className={styles.section} data-testid="overview-request-answers">
          <h3 className={styles.sectionTitle}>From the request</h3>
          <RequestAnswers {...answers} />
        </section>
      )}

      <section className={styles.section} data-testid="overview-tags">
        <h3 className={styles.sectionTitle}>Tags</h3>
        <DemandTagPicker demand={demand} canEdit={canEditCouncilFields} />
      </section>

      <section className={styles.section} data-testid="overview-success">
        <h3 className={styles.sectionTitle}>What success looks like</h3>
        <div className={styles.kvRow}>
          <span className={styles.kvKey}>Anticipated benefit</span>
          <div className={styles.kvValue}>
            <EditableField
              value={demand.anticipated_benefit ?? ''}
              path="anticipated_benefit"
              onSave={onSave}
              disabled={!canEditAnswers}
              multiline
              placeholder="What outcome does success unlock?"
              ariaLabel="Anticipated benefit"
            />
          </div>
        </div>
        <div className={styles.kvRow}>
          <span className={styles.kvKey}>Business criticality</span>
          <div className={styles.kvValue}>
            <EditableField
              value={demand.business_criticality ?? ''}
              path="business_criticality"
              onSave={onSave}
              disabled={!canEditCouncilFields}
              multiline
              placeholder="How critical is this?"
              ariaLabel="Business criticality"
            />
          </div>
        </div>
      </section>
    </div>
  )
}
